// Phase 3 server data: reports, mail, the leaderboards, titles, help, nearby guilds — plus the mutations that touch them.
// Guild actions go through useCityActions (they answer with the refreshed city); the hooks here are the reads and the
// read/delete bookkeeping of the two inboxes.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { GuildMembersRankingResult, MailList, MailThread, RankingsResult, ReportsList, UserTitlesResult } from "@/api/types";
import { keys as villageKeys } from "./queries";

export const socialKeys = {
  reports: (wid: number) => ["reports", wid] as const,
  mail: (wid: number) => ["mail", wid] as const,
  thread: (wid: number, id: string) => ["mail-thread", wid, id] as const,
  board: (wid: number, cls: string, info: string, page: number) => ["rankings", wid, cls, info, page] as const,
  members: (wid: number) => ["guild-members-rank", wid] as const,
  titles: (uid: number) => ["titles", uid] as const,
  help: ["help"] as const,
  nearby: (wid: number, vid: number) => ["guild-nearby", wid, vid] as const,
};

// ------------------------------------------------------------------ reports

/** The latest 100 reports (the server marks the inbox seen when it answers). Polled every two minutes while open. */
export function useReports(wid: number, enabled = true) {
  return useQuery({
    queryKey: socialKeys.reports(wid),
    queryFn: () => api.reports.list({ wid }),
    enabled: enabled && !!wid,
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });
}

export function useReportMutations(wid: number) {
  const qc = useQueryClient();
  const patch = (fn: (r: ReportsList) => ReportsList) => qc.setQueryData<ReportsList>(socialKeys.reports(wid), (old) => (old ? fn(old) : old));
  /** the city's new_report flag is stale once the list is read */
  const clearFlag = () => qc.setQueriesData({ queryKey: villageKeys.villagesOfWorld(wid) }, (old: unknown) => (old && typeof old === "object" && "new_report" in (old as object) ? { ...(old as object), new_report: 0 } : old));
  const markRead = useMutation({
    mutationFn: (id: string) => api.reports.markRead({ wid }, id),
    onMutate: (id) => patch((r) => ({ ...r, report_info: { ...r.report_info, newsfeed: { ...r.report_info.newsfeed, [id]: { ...r.report_info.newsfeed[id], r: 1 } } } })),
  });
  const markAllRead = useMutation({
    mutationFn: async (ids: string[]) => { for (const id of ids) await api.reports.markRead({ wid }, id); },
    onMutate: (ids) => patch((r) => { const nf = { ...r.report_info.newsfeed }; for (const id of ids) if (nf[id]) nf[id] = { ...nf[id], r: 1 }; return { ...r, report_info: { ...r.report_info, newsfeed: nf } }; }),
  });
  const remove = useMutation({
    mutationFn: (ids: string[]) => api.reports.delete({ wid }, ids),
    onSuccess: (_r, ids) => patch((r) => { const nf = { ...r.report_info.newsfeed }; for (const id of ids) delete nf[id]; return { ...r, report_info: { newsfeed: nf, nf_order: r.report_info.nf_order.filter((x) => !ids.includes(String(x))) } }; }),
  });
  return { markRead, markAllRead, remove, clearFlag };
}

// ------------------------------------------------------------------ mail

export function useMail(wid: number, enabled = true) {
  return useQuery({
    queryKey: socialKeys.mail(wid),
    queryFn: () => api.mail.list({ wid }),
    enabled: enabled && !!wid,
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });
}

/** One conversation's letters (the server marks it read when it answers). */
export function useMailThread(wid: number, id: string | null) {
  return useQuery({
    queryKey: socialKeys.thread(wid, id ?? ""),
    queryFn: () => api.mail.view({ wid }, id!),
    enabled: !!wid && !!id,
    staleTime: 15_000,
    retry: false,
  });
}

export function useMailMutations(wid: number) {
  const qc = useQueryClient();
  const patchList = (fn: (r: MailList) => MailList) => qc.setQueryData<MailList>(socialKeys.mail(wid), (old) => (old ? fn(old) : old));
  const markRead = (id: string) => patchList((r) => ({ output: { newsfeed: r.output.newsfeed.map((m) => (String(m.i) === id ? { ...m, r: 1 } : m)) } }));
  const send = useMutation({
    mutationFn: (p: { to: string; subject: string; body: string }) => api.mail.send({ wid }, p.to, p.subject, p.body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: socialKeys.mail(wid) }),
  });
  const reply = useMutation({
    mutationFn: (p: { id: string; body: string }) => api.mail.reply({ wid }, p.id, p.body),
    onSuccess: (_r, p) => {
      void qc.invalidateQueries({ queryKey: socialKeys.thread(wid, p.id) });
      void qc.invalidateQueries({ queryKey: socialKeys.mail(wid) });
    },
  });
  const remove = useMutation({
    mutationFn: (ids: string[]) => api.mail.delete({ wid }, ids),
    onSuccess: (_r, ids) => {
      patchList((r) => ({ output: { newsfeed: r.output.newsfeed.filter((m) => !ids.includes(String(m.i))) } }));
      for (const id of ids) qc.removeQueries({ queryKey: socialKeys.thread(wid, id) });
    },
  });
  /** keep a thread's cache in step after a reply without a refetch */
  const appendLocal = (id: string, msg: MailThread["output"]["mail"][number]) => qc.setQueryData<MailThread>(socialKeys.thread(wid, id), (old) => (old ? { ...old, output: { mail: [...old.output.mail, msg] } } : old));
  return { send, reply, remove, markRead, appendLocal };
}

// ------------------------------------------------------------------ leaderboards

export type BoardClass = "user" | "guild";
export type BoardInfo = "score" | "ksa" | "ksd" | "kst";

/** One page of a world board; page 0 = the page you (your guild) are on. */
export function useBoard(wid: number, cls: BoardClass, info: BoardInfo, page: number, enabled = true) {
  return useQuery<RankingsResult>({
    queryKey: socialKeys.board(wid, cls, info, page),
    queryFn: () => api.rankings.player({ wid }, { lb_class: cls, lb_info: info, page_num: page }),
    enabled: enabled && !!wid,
    staleTime: 120_000,
    placeholderData: (prev) => prev,
  });
}

/** Your guild's members ranked by points (rankings/guild). */
export function useGuildMembersRanking(wid: number, enabled = true) {
  return useQuery<GuildMembersRankingResult>({
    queryKey: socialKeys.members(wid),
    queryFn: () => api.rankings.guild({ wid }),
    enabled: enabled && !!wid,
    staleTime: 120_000,
  });
}

// ------------------------------------------------------------------ titles, help, nearby guilds

export function useTitles(uid: number | null, enabled = true) {
  return useQuery<UserTitlesResult>({
    queryKey: socialKeys.titles(uid ?? 0),
    queryFn: () => api.titles.get(uid!),
    enabled: enabled && !!uid && uid > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useTitleMutations(uid: number | null) {
  const qc = useQueryClient();
  const done = () => { if (uid) void qc.invalidateQueries({ queryKey: socialKeys.titles(uid) }); };
  const setMain = useMutation({ mutationFn: (p: { title: string; tier: number | string }) => api.titles.setMain(p.title, p.tier), onSuccess: done });
  const setPrivacy = useMutation({ mutationFn: (privacy: 0 | 1) => api.titles.togglePrivacy(privacy), onSuccess: done });
  return { setMain, setPrivacy };
}

export function useHelp(enabled = true) {
  return useQuery({ queryKey: socialKeys.help, queryFn: () => api.help(), enabled, staleTime: Infinity });
}

export function useNearbyGuilds(wid: number, vid: number | null, enabled = true) {
  return useQuery({
    queryKey: socialKeys.nearby(wid, vid ?? 0),
    queryFn: async () => {
      const r = await api.guild.nearby({ wid, vid: vid! });
      return r.nearby_guilds ?? r.guilds ?? [];
    },
    enabled: enabled && !!wid && !!vid,
    staleTime: 5 * 60_000,
  });
}
